// Copyright (c) 2025 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { catchError, filter, mergeMap } from "rxjs/operators"
import { grpc } from "../../../app/rpc/executor"
import { showSnackBar } from "../../snackbar/actions"
import { timesheetFieldClient } from "../../../app/rpc/client"
import {
  BatchGetTimesheetFieldConfigsRequest,
  GenerateTimesheetFieldConfigsRequest,
  GenerateTimesheetFieldConfigsResponse,
  TimesheetFieldConfigRef,
} from "../../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"
import * as Actions from "../actions"

type GenerateTimesheetFieldConfigsRpc = (request: GenerateTimesheetFieldConfigsRequest) => Observable<GenerateTimesheetFieldConfigsResponse>

export const generateTimesheetFieldConfigs = (generateTimesheetFieldConfigsRpc: GenerateTimesheetFieldConfigsRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.generateTimesheetFieldConfigs.match),
      mergeMap(action => {
        const request = action.payload

        return generateTimesheetFieldConfigsRpc(request).pipe(
          mergeMap(response => {
            const actions: Action[] = []

            const { configRefsList } = response.toObject()
            if (configRefsList.length) {
              actions.push(
                Actions.batchGetTimesheetFieldConfigs(
                  new BatchGetTimesheetFieldConfigsRequest()
                    .setSiteId(request.getSiteId())
                    .setConfigRefsList(
                      configRefsList.map(configRef => (
                        new TimesheetFieldConfigRef()
                          .setTimesheetFieldId(configRef.timesheetFieldId)
                          .setConfigHash(configRef.configHash)
                      ))
                    )
                ),
              )
            }

            return concat(actions)
          }),
          catchError(() => of(showSnackBar({
            message: t({
              id: "msg_generate_timesheet_field_configs_failed",
              message: "Failed to load timesheet field settings. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
            }),
            messageType: "ERROR",
          }))),
        )
      }),
    )

const generateTimesheetFieldConfigsRpc = (request: GenerateTimesheetFieldConfigsRequest): Observable<GenerateTimesheetFieldConfigsResponse> =>
  grpc(() => timesheetFieldClient.generateTimesheetFieldConfigs(request, null))

export default generateTimesheetFieldConfigs(generateTimesheetFieldConfigsRpc)
